import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Area, ComposedChart, Legend, Line, Tooltip, XAxis, YAxis } from 'recharts'
import { ResponsiveContainer } from 'recharts'

import { Coupon, CouponWithPayments } from '@/api/affiliate/types/Coupon'
import { MiniPkgPayment } from '@/api/payments/types/PkgPayment'
import { PkgWarehouse } from '@/api/user-package/types/UserPkg'
import { Card, CardContent } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { WarehousePreview } from '@/components/WarehousePreview'
import { formatDateEuUtc, formatDateShortEuUtc, formatMonthYearEuUtc } from '@/helpers/formatDate'
import { cn } from '@/lib/utils'

const PIE_COLORS = ['#2EA8D7', '#C2D6E3', '#B79C5E', '#DAD8D5', '#F4D400', '#ECE8D3', '#BFD8E8']
const TOTAL_KEY = 'Total'
const TOTAL_COLOR = '#111827'

const calcRevenueFromPayment = (payment: MiniPkgPayment, coupon: Coupon) => {
  const couponRevenue = payment.couponRevenue ?? (coupon.revenue ? coupon.revenue : 0)
  return couponRevenue * ((payment.shippingPaid || 0) - (payment.refundAmount || 0))
}
interface AffiliateCouponCardProps {
  coupons: CouponWithPayments[]
  allowDailyChart?: boolean
}
export const AffiliateCouponSection = ({ coupons, allowDailyChart = false }: AffiliateCouponCardProps) => {
  const { t } = useTranslation()
  const [isDailyView, setIsDailyView] = useState(false)
  // Get coupon codes for chart areas
  const couponCodes = useMemo(() => {
    return coupons?.map(coupon => coupon.code) || []
  }, [coupons])
  const couponByCodeByCountry = useMemo(() => {
    if (!coupons) return []
    return coupons.map(coupon => {
      const couponContries = coupon.country
        .filter(country => country !== 'all')
        .reduce(
          (acc, country) => {
            acc[country] = []
            return acc
          },
          {} as Record<string, MiniPkgPayment[]>
        )
      // Group payments by warehouse/country
      const paymentsByCountry =
        coupon.payments?.reduce((acc, payment) => {
          const warehouse = payment.warehouse
          if (!warehouse) return acc
          if (!acc[warehouse]) {
            acc[warehouse] = []
          }
          acc[warehouse].push(payment)
          return acc
        }, couponContries) || couponContries

      // Calculate total usage and revenue across all countries
      const totalUsage = coupon.payments?.length || 0
      const totalRevenue = coupon.payments?.reduce((sum, payment) => sum + calcRevenueFromPayment(payment, coupon), 0) || 0

      // Create array starting with total row
      const result: Array<{
        couponCode: string
        country: string | null
        payments: MiniPkgPayment[]
        usage: number
        revenue: number
        couponRevenue: string
        couponCountries: (PkgWarehouse | 'all')[]
        isTotal: boolean
      }> = [
        {
          couponCode: coupon.code,
          country: null,
          payments: coupon.payments || [],
          usage: totalUsage,
          revenue: totalRevenue,
          couponRevenue: coupon.revenue.toString(),
          couponCountries: coupon.country,
          isTotal: true,
        },
      ]

      // Add per-country rows only if there are multiple countries
      const countryEntries = Object.entries(paymentsByCountry)
      if (countryEntries.length > 1) {
        countryEntries.forEach(([country, payments]) => {
          // Calculate revenue for this country
          const countryRevenue = payments.reduce((sum, payment) => sum + calcRevenueFromPayment(payment, coupon), 0)

          result.push({
            couponCode: coupon.code,
            country,
            payments,
            usage: payments.length,
            revenue: countryRevenue,
            couponRevenue: coupon.revenue.toString(),
            couponCountries: coupon.country,
            isTotal: false,
          })
        })
      }

      return result
    })
  }, [coupons])
  // Process payments data for chart - group by coupon code and month or day
  const chartData = useMemo(() => {
    if (!coupons || coupons.length === 0) return []

    // Get all unique time periods (months or days) across all coupons
    const allTimePeriods = new Set<string>()
    const couponDataMap: Record<string, Record<string, { usage: number; revenue: number }>> = {}

    coupons.forEach(coupon => {
      if (coupon.payments && Array.isArray(coupon.payments)) {
        const couponCode = coupon.code
        if (!couponDataMap[couponCode]) {
          couponDataMap[couponCode] = {}
        }

        coupon.payments.forEach((payment: MiniPkgPayment) => {
          if (payment.createdAt) {
            // Extract time period key in UTC: daily YYYY-MM-DD or monthly YYYY-MM
            const d = new Date(payment.createdAt)
            const timeKey = isDailyView
              ? `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
              : `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
            allTimePeriods.add(timeKey)

            if (!couponDataMap[couponCode][timeKey]) {
              couponDataMap[couponCode][timeKey] = { usage: 0, revenue: 0 }
            }

            // Calculate revenue: couponRevenue * shippingPaid or use revenue field
            const revenue = calcRevenueFromPayment(payment, coupon)
            couponDataMap[couponCode][timeKey].usage += 1
            couponDataMap[couponCode][timeKey].revenue += revenue
          }
        })
      }
    })

    // Create chart data with all time periods and coupon codes
    const sortedTimePeriods = Array.from(allTimePeriods).sort()
    return sortedTimePeriods.map(timePeriod => {
      const dataPoint: Record<string, string | number> = { date: timePeriod }
      let totalUsage = 0
      Object.keys(couponDataMap).forEach(couponCode => {
        const couponData = couponDataMap[couponCode][timePeriod]

        if (couponData) {
          dataPoint[`${couponCode}_usage`] = couponData.usage
          dataPoint[`${couponCode}_revenue`] = Math.round(couponData.revenue)
          totalUsage += couponData.usage
        } else {
          dataPoint[`${couponCode}_usage`] = 0
          dataPoint[`${couponCode}_revenue`] = 0
        }
      })
      dataPoint[`${TOTAL_KEY}_usage`] = totalUsage
      return dataPoint
    })
  }, [coupons, isDailyView])

  return (
    <>
      <Card className='bg-white'>
        <CardContent className='space-y-2'>
          {coupons && coupons.length > 0 ? (
            <>
              <p className='text-text-secondary font-medium'>{t('affiliate.couponDetails')}</p>
              <div className='border-border-default overflow-hidden rounded-lg border px-3'>
                <div className='border-border-default text-text-secondary grid grid-cols-3 gap-4 border-b py-3'>
                  <div className='text-sm font-medium'>{t('affiliate.couponName')}</div>
                  <div className='text-center text-sm font-medium'>{t('affiliate.usage')}</div>
                  <div className='text-end text-sm font-medium'>{t('affiliate.revenue')}</div>
                </div>
                {couponByCodeByCountry.map((couponRows, couponIdx) => {
                  const hasMultipleCountries = couponRows.filter(r => !r.isTotal).length > 1
                  return (
                    <div key={couponIdx} className='border-border-default border-b py-3 last:border-0'>
                      {couponRows.map((row, rowIdx) => {
                        // Only show per-country rows if there are multiple countries
                        if (!row.isTotal && !hasMultipleCountries) {
                          return null
                        }

                        return (
                          <div key={rowIdx} className={`grid grid-cols-3 gap-4 ${!row.isTotal ? 'mt-2' : ''}`}>
                            <div className='flex items-center gap-2 text-sm'>
                              <div className='flex items-center -space-x-2'>
                                {row.isTotal
                                  ? // Show all countries for total row
                                    row.couponCountries.includes('all')
                                    ? Object.values(PkgWarehouse).map(country => (
                                        <WarehousePreview
                                          className='border-border-default rounded-full border'
                                          key={country}
                                          warehouse={country}
                                        />
                                      ))
                                    : row.couponCountries.map(country => (
                                        <WarehousePreview key={country} warehouse={country as PkgWarehouse} />
                                      ))
                                  : // Show single country for per-country row
                                    row.country && (
                                      <WarehousePreview
                                        containerClassName='text-text-secondary'
                                        includeLabel
                                        warehouse={row.country as PkgWarehouse}
                                      />
                                    )}
                              </div>
                              {row.isTotal ? row.couponCode : ''}
                            </div>
                            <div className={cn('text-center text-sm', !row.isTotal && 'text-text-secondary')}>{row.usage}</div>
                            <div className={cn('text-end text-sm', !row.isTotal && 'text-text-secondary')}>{Math.round(row.revenue)}₪</div>
                          </div>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            </>
          ) : (
            <div className='text-text-secondary py-4 text-center'>{t('affiliate.noCouponsToShow')}</div>
          )}
        </CardContent>
      </Card>
      {chartData.length > 0 && couponCodes.length > 0 && (
        <Card className='bg-white'>
          <CardContent className='space-y-2'>
            {/* Chart Section */}
            <div className='mt-6'>
              <div className='mb-4 flex items-center justify-between'>
                <p className='text-text-secondary font-medium'>{t('affiliate.couponsChartTitle')}</p>
                {allowDailyChart && (
                  <div className='flex items-center gap-2'>
                    <label htmlFor='daily-chart-toggle' className='text-text-secondary cursor-pointer text-sm'>
                      {t('affiliate.monthly')}
                    </label>
                    <Switch id='daily-chart-toggle' checked={isDailyView} onCheckedChange={setIsDailyView} />
                    <label htmlFor='daily-chart-toggle' className='text-text-secondary cursor-pointer text-sm'>
                      {t('affiliate.daily')}
                    </label>
                  </div>
                )}
              </div>
              <div dir='ltr' className='bg-white'>
                <ResponsiveContainer width='100%' height={350}>
                  <ComposedChart data={chartData}>
                    <defs>
                      {couponCodes.map((code, idx) => {
                        const color = PIE_COLORS[idx % PIE_COLORS.length]
                        return (
                          <linearGradient key={code} id={`color${code}`} x1='0' y1='0' x2='0' y2='1'>
                            <stop offset='5%' stopColor={color} stopOpacity={0.4} />
                            <stop offset='95%' stopColor={color} stopOpacity={0.05} />
                          </linearGradient>
                        )
                      })}
                    </defs>
                    <XAxis
                      dataKey='date'
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12 }}
                      tickFormatter={value => {
                        if (isDailyView) {
                          return formatDateShortEuUtc(value)
                        }
                        return formatMonthYearEuUtc(`${value}-01`)
                      }}
                    />
                    <YAxis type='number' tick={{ fontSize: 12 }} width={30} axisLine={false} tickLine={false} />
                    <Tooltip
                      labelFormatter={label => {
                        if (isDailyView) {
                          return formatDateEuUtc(label)
                        }
                        return formatMonthYearEuUtc(`${label}-01`)
                      }}
                      content={({ active, payload, label }) => {
                        if (!active || !payload || !payload.length) return null

                        const couponData: Record<string, { usage: number; revenue: number }> = {}
                        const items = payload[0].payload
                        Object.entries(items).forEach(([key, value]) => {
                          const match = key.match(/^(.+)_(usage|revenue)$/)
                          if (match) {
                            const [, couponCode, type] = match
                            if (couponCode === TOTAL_KEY) return
                            if (!couponData[couponCode]) {
                              couponData[couponCode] = { usage: 0, revenue: 0 }
                            }
                            if (type === 'usage') {
                              couponData[couponCode].usage = Number(value) || 0
                            }

                            if (type === 'revenue') {
                              couponData[couponCode].revenue = Number(value) || 0
                            }
                          }
                        })

                        let formattedDate: string
                        if (isDailyView) {
                          formattedDate = formatDateEuUtc(label)
                        } else {
                          formattedDate = formatMonthYearEuUtc(`${label}-01`)
                        }
                        const couponEntries = Object.entries(couponData)
                        const totalUsage =
                          Number(items[`${TOTAL_KEY}_usage`]) || couponEntries.reduce((sum, [, data]) => sum + data.usage, 0)
                        const totalRevenueVal = couponEntries.reduce((sum, [, data]) => sum + data.revenue, 0)
                        return (
                          <div dir='rtl' className='border-border-default rounded-lg border bg-white p-3 shadow-lg'>
                            <div className='text-text-primary mb-2 font-medium'>{formattedDate}</div>
                            {couponEntries.length > 1 && (
                              <div className='border-border-default mb-1 border-b pb-1 text-sm'>
                                <div className='text-text-primary font-medium'>{t('affiliate.total')}</div>
                                <div className='text-text-secondary'>{t('affiliate.usageCount', { count: totalUsage })}</div>
                                <div className='text-text-secondary'>{t('affiliate.revenueAmount', { amount: totalRevenueVal })}</div>
                              </div>
                            )}
                            {couponEntries.map(
                              ([code, data]) =>
                                data.usage > 0 && (
                                  <div key={code} className='mb-1 text-sm'>
                                    <div className='text-text-primary font-medium'>{code}</div>
                                    <div className='text-text-secondary'>{t('affiliate.usageCount', { count: data.usage })}</div>
                                    {data.revenue > 0 && (
                                      <div className='text-text-secondary'>{t('affiliate.revenueAmount', { amount: data.revenue })}</div>
                                    )}
                                  </div>
                                )
                            )}
                          </div>
                        )
                      }}
                    />
                    <Legend
                      iconType='line'
                      className='flex flex-wrap items-center'
                      formatter={value => {
                        const match = value.match(/^(.+)_usage$/)
                        return match ? match[1] : value
                      }}
                    />
                    {couponCodes.map((code, idx) => {
                      const color = PIE_COLORS[idx % PIE_COLORS.length]
                      return (
                        <Area
                          key={code}
                          type='monotone'
                          dataKey={`${code}_usage`}
                          name={`${code}_usage`}
                          stroke={color}
                          strokeWidth={2}
                          fillOpacity={0.35}
                          fill={`url(#color${code})`}
                        />
                      )
                    })}
                    {couponCodes.length > 1 && (
                      <Line
                        type='monotone'
                        dataKey={`${TOTAL_KEY}_usage`}
                        name={`${TOTAL_KEY}_usage`}
                        stroke={TOTAL_COLOR}
                        strokeWidth={2.5}
                        strokeDasharray='6 4'
                        dot={false}
                      />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  )
}
